/**
 * Class that converts native select element to combobox data source.
 *
 * DataSource is an interface that provides following methods:
 *
 * - `getContent():object[]` - Returns all available data.
 * - `setValue(string[]):void` - Updates data source selection state.
 * - `getValue():string[]` - Get current selection state from data source.
 * - `on(string, function):void` - Register listener with data source.
 *
 * DataSource must trigger following events:
 *
 * - `update` - When items or item groups were updated.
 * - `change` - When selected state was changed in data source.
 * - `content` - If data source content objects are added, removed or recreated.
 *
 * SelectDataSource is intended default data source for Combobox, but can be replaced with any implementation that implements required methods.
 *
 * - Uses MutationObserver to listen changes in native select and triggers custom events based on changes
 *    - Changes in attributes `hidden` or `disabled` will trigger internal `update` event
 *    - Changes in childlist (additions/removals) will trigger internal `content` event. Note! Internal content object is recreated when childlist changes.
 * - Listens select `change` event, updates internal state and triggers internal `change`
 * - Internal value can be updated with {@link SelectDataSource.setValue} method. Also updates select element and triggers `change` event.
 * - {@link SelectDataSource.on} method can be used to register event listener
 * @implements {DataSource}
 */
class SelectDataSource {

  /**
   * @type {HTMLSelectElement}
   */
  #select = null;

  /**
   * @type {MutationObserver}
   */
  #observer = null;

  #itemsCreated = 0;
  #groupsCreated = 0;

  #listeners = [];
  #data = [];
  #onNativeSelectChange = null;
  #ignoreNextOnChange = false;

  constructor(select) {
    this.#select = select;
    this.#init();
  }

  /**
   * Get original native select element.
   *
   * @returns {HTMLSelectElement} Native select element.
   */
  getNativeSelect() {
    return this.#select;
  }

  #init() {
    this.cloneFromSource();
    this.#createMutationObserver();
    const select = this.getNativeSelect();

    const onChange = (e) => {
      if (this.#ignoreNextOnChange) {
        this.#ignoreNextOnChange = false;
        return;
      }

      const value = this.getValue();
      this.setValue(value);

      // Trigger event
      this.#trigger('change', [value]);
    };
    this.#onNativeSelectChange = onChange.bind(this);
    select.addEventListener('change', this.#onNativeSelectChange);

    // Update selections after form reset
    if (select.form) {
      select.form.addEventListener('reset', this.#onNativeSelectChange);
    }
  }

  /**
   * Create mutation observer that listens native select for changes.
   */
  #createMutationObserver() {
    if (this.#observer) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      const isChildListChange = mutations.find(mutation => mutation.type === 'childList');
      if (isChildListChange) {
        this.cloneFromSource();

        // If mutation contains childList change AND select attribute change, don't forget the attributes.
        const selectAttributeMutations = mutations.filter(mutation => mutation.type === 'attributes' && mutation.target.tagName.toLowerCase() === 'select');
        if(selectAttributeMutations.length > 0) {
          const attributeMutations = selectAttributeMutations.map(mutation => {
            return {
              type: 'select',
              updatedItem: null,
              attribute: mutation.attributeName,
              value: mutation.target[mutation.attributeName],
              oldValue: mutation.oldValue,
            }
          });
          this.#trigger('update', [attributeMutations]);
        }
      }
      else {
        const updates = mutations.map((mutation) => {
          const { type, target } = mutation;
          const targetTagName = target.tagName.toLowerCase();

          if (type === 'attributes') {
            const updatedItem = targetTagName === 'optgroup' ? this.#findGroup(target.label) : (targetTagName === 'option' ? this.#findItem(target.value) : null);
            const value = target[mutation.attributeName];

            if (updatedItem) {
              updatedItem[mutation.attributeName] = value;
            }

            if (targetTagName === 'select') {
              return { type: 'select', updatedItem, attribute: mutation.attributeName, value: value, oldValue: mutation.oldValue };
            }
            else if (targetTagName === 'optgroup') {
              return { type: 'group', updatedItem, attribute: mutation.attributeName, value: value, oldValue: mutation.oldValue };
            }
            else if (targetTagName === 'option') {
              return { type: 'option', updatedItem, attribute: mutation.attributeName, value: value, oldValue: mutation.oldValue };
            }
          }
        });

        this.#trigger('update', [updates]);
      }
    });

    observer.observe(this.getNativeSelect(), {
      attributes: true,
      attributeFilter: ['disabled', 'hidden'],
      subtree: true,
      childList: true,
    });

    this.#observer = observer;
  }


  /**
   * Calls event listeners.
   *
   * @param {string} name Event name.
   * @param {array} data Array of data to pass to callback.
   */
  #trigger(name, data = []) {
    this.#listeners.filter(item => item.name === name).forEach(item => {
      item.callback(...data);
    });
  }

  #findGroup(label) {
    return this.getContent().find(item => {
      return item.label === label;
    });
  }

  #findItem(value) {
    return this.getContent().find(item => {
      if (item.items) {
        return item.items.find(child => child.value === value);
      }

      return item.value === value;
    });
  }

  /**
   * Add listener to this datasource.
   *
   * @param {string} name Event name.
   * @param {function} listener Callback function.
   */
  on(name, listener) {
    this.#listeners.push({ name, callback: listener });
  }

  /**
   * Get native select value.
   *
   * @returns {string[]} Current select value.
   */
  getValue() {
    return Array.from(this.getNativeSelect().selectedOptions).map(item => item.value);
  }

  /**
   * Changes native select value.
   *
   * @param {string[]} value Value to set.
   */
  setValue(value) {
    this.getContent().forEach(item => {
      if (item.items) {
        item.items.forEach(child => {
          child.selected = value.includes(child.value);
        });
      }
      else {
        item.selected = value.includes(item.value);
      }
    });

    let changed = false;
    Array.from(this.getNativeSelect().options).forEach(option => {
      if (value.includes(option.value)) {
        if (!option.selected) {
          changed = true;
          option.selected = true;
        }
      }
      else {
        if (option.selected) {
          changed = true;
          option.selected = false;
        }
      }
    });

    if (changed) {
      this.#ignoreNextOnChange = true;
      this.getNativeSelect().dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Converts native select element to combobox internal format.
   */
  cloneFromSource() {
    this.#data = [];
    const createItem = (item) => {
      this.#itemsCreated++;
      return {
        id: (this.getNativeSelect().id || 'select') + '-option-' + this.#itemsCreated,
        value: item.value,
        text: item.innerText.trim(),
        disabled: item.disabled,
        hidden: item.hidden,
        selected: item.selected
      };
    };

    for (let item of this.getNativeSelect().children) {
      if (item.tagName === 'OPTGROUP') {
        this.#groupsCreated++;
        const data = {
          id: (this.getNativeSelect().id || 'select') + '-option-' + this.#groupsCreated,
          label: item.label,
          disabled: item.disabled,
          hidden: item.hidden,
          items: []
        };

        for (let child of item.children) {
          data.items.push(createItem(child));
        }

        this.#data.push(data);
      }
      else if (item.tagName === 'OPTION') {
        this.#data.push(createItem(item));
      }
    }

    this.#trigger('content');
  }

  /**
   * Get all items and groups.
   *
   * @returns {object[]} Array of content.
   */
  getContent() {
    return this.#data;
  }
}

export default SelectDataSource;
